import { Api } from "@/api";
import {
  CustomerChannelDocument,
  RegistrationDocument,
  SendRecoveryPasswordMailDocument,
  B2cCustomer,
  SalesChannelOptions,
  RecoveryPasswordDocument,
  B2cCustomerChannelPayload,
} from "@/generated/graphql.lazy/b2c";
import { defaultCurrency } from "@/constants/currency";
import { defaultVatCoefficient } from "@/constants/vat";
import { prepareFormErrors } from "@/util/graphql-errors-parser";
import { Context } from "@/services/context.ts";
import { supportedLanguages } from "@/constants/languages.ts";
import { Services } from "@/services/services.ts";

export const CUSTOMER_CHANNEL_LISTENER_NAME = "customerChannel";

export type B2cFooterContactsType = {
  businessName: string;
  email: string;
  address: string;
  regNr: string;
  vatNr: string;
  hasAnyContactFieldFilled: boolean;
};

export class CustomerChannel {
  isLoaded = false;

  customer = null as B2cCustomer | null;
  channelOptions = null as SalesChannelOptions | null;
  allowedCurrencyCodes: string[] = [];
  footerAboutPages: string[] = [];
  languages: string[] = [];
  isAllowedAllCurrencies = false;
  isUnitedSearchResult = false;
  vatCoefficient: number = defaultVatCoefficient;
  availableCountries: { id: string; name: string; code: string; vatRate: number }[] = [];
  currentCountry: { id: string; name: string; code: string } | null = null;
  colorScheme: string | undefined;
  template: string | undefined;
  logoUrl: string | undefined;
  whatsappPhone: string | undefined;
  contactEmail = "";
  showShippingRates = true;
  footerContacts: B2cFooterContactsType | null = null;
  footerContactsAdditional: B2cFooterContactsType | null = null;

  public currencyCode = defaultCurrency;

  constructor(
    protected context: Context,
    protected api: Api,
    protected services: Services,
  ) {}

  private update = () => {
    this.context.notify(CUSTOMER_CHANNEL_LISTENER_NAME);
  };

  private get value() {
    return {
      ...this,
    };
  }

  isCustomerAuth = () => {
    return !!this?.customer;
  };

  customerCurrencyCode() {
    if (this.isAllowedAllCurrencies) {
      return this.currencyCode;
    }

    if (!this.allowedCurrencyCodes.includes(this.currencyCode)) {
      return defaultCurrency;
    }

    return this.currencyCode;
  }

  fetchCustomerChannel = async () => {
    const response = await this.api.client.query({
      query: CustomerChannelDocument,
      variables: {},
    });

    this.setPreloadedCustomerChannel(response.data.CustomerChannel as B2cCustomerChannelPayload);
  };

  fetchIfNotLoaded = async () => {
    if (!this.isLoaded) {
      await this.fetchCustomerChannel();
    }
  };

  setPreloadedCustomerChannel = (CustomerChannel: B2cCustomerChannelPayload) => {
    this.customer = CustomerChannel.customer;
    this.channelOptions = CustomerChannel.salesChannel;
    this.currencyCode = this.customer?.currency ?? this.channelOptions?.currency.code;
    this.allowedCurrencyCodes = CustomerChannel?.availableCountries.map((country) => country.currencyCode);
    this.languages = CustomerChannel?.languages ?? supportedLanguages.map((lang) => lang.code);
    this.isAllowedAllCurrencies = this.allowedCurrencyCodes.length <= 0;
    this.vatCoefficient = CustomerChannel.vatCoefficient;
    this.availableCountries = CustomerChannel.availableCountries;
    this.footerAboutPages = CustomerChannel.footerAboutPages ?? [];
    this.currentCountry = CustomerChannel.currentCountry;
    this.logoUrl = CustomerChannel.logoUrl;
    this.colorScheme = CustomerChannel.colorScheme;
    this.template = CustomerChannel.template;
    this.isLoaded = true;
    this.whatsappPhone = CustomerChannel.whatsappPhone;
    this.isUnitedSearchResult = CustomerChannel.isUnitedSearchResult;
    this.contactEmail = CustomerChannel.contactEmail;
    this.showShippingRates = CustomerChannel.showShippingRates !== false;
    this.footerContacts = CustomerChannel.footerContacts;
    this.footerContactsAdditional = CustomerChannel.footerContactsAdditional;
    this.services.jsonld.load();

    this.update();
  };

  clear() {
    this.isLoaded = false;
    this.customer = null;
    this.channelOptions = null;
    this.allowedCurrencyCodes = [];
    this.isAllowedAllCurrencies = false;
    this.isUnitedSearchResult = false;
    this.vatCoefficient = defaultVatCoefficient;
    this.availableCountries = [];
    this.footerAboutPages = [];
    this.currentCountry = null;
    this.contactEmail = "";
    this.showShippingRates = true;
    this.footerContacts = null;
    this.footerContactsAdditional = null;
  }

  setDefaultVat(vat: number) {
    if (this.vatCoefficient === vat) {
      return;
    }

    this.vatCoefficient = vat;
    this.context.notify(CUSTOMER_CHANNEL_LISTENER_NAME);
  }

  login = async (data: { email: string; password: string }) => {
    const url = `${this.api.base}/customer/auth`;
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify({
        email: data.email,
        password: data.password,
      }),
      credentials: "include",
    });
    const success = response.status === 204;
    const result = success ? { errors: [] } : ((await response.json()) as { errors: string[] });
    return {
      success,
      errors: result.errors,
    };
  };

  logout = async () => {
    const url = `${this.api.base}/customer/logout`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
    });
    const success = response.status === 200;
    const result = success ? { errors: [] } : ((await response.json()) as { errors: string[] });

    this.customer = null;
    this.update();

    return {
      success,
      errors: result.errors,
    };
  };

  signup = async (input: {
    email: string;
    password: string;
    phone: string;
    newsletter: boolean;
    countryId: string;
    currencyId: string;
    locale: string;
    isCompany: boolean;
    vatNumber: string;
    billingEmail: string;
  }) => {
    const response = await this.api.client.mutate({
      mutation: RegistrationDocument,
      variables: { input },
    });

    if (response.errors) {
      return {
        success: false,
        errors: prepareFormErrors(response.errors),
      };
    }

    return {
      success: true,
      data: response.data!.Registration,
    };
  };

  requestRecoveryPassword = async (input: { email: string }) => {
    const response = await this.api.client.mutate({
      mutation: SendRecoveryPasswordMailDocument,
      variables: { input },
    });

    if (response.errors) {
      return {
        success: false,
        errors: response.errors,
      };
    }

    return {
      success: true,
      data: response.data!.SendRecoveryPasswordMail,
    };
  };

  resetPassword = async (input: { newPassword: string; confirmedPassword: string; resetPasswordToken: string }) => {
    const response = await this.api.client.mutate({
      mutation: RecoveryPasswordDocument,
      variables: { input },
    });

    if (response.errors) {
      return {
        success: false,
        errors: response.errors,
      };
    }

    return {
      success: true,
      data: response.data!.RecoveryPassword,
    };
  };
}
